export enum ErrorCode {
  OK = 0,
  Canceled = 1,
  Unknown = 2,
  InvalidArgument = 3,
  DeadlineExceeded = 4,
  NotFound = 5,
  AlreadyExists = 6,
  PermissionDenied = 7,
  ResourceExhausted = 8,
  FailedPrecondition = 9,
  Aborted = 10,
  OutOfRange = 11,
  Unimplemented = 12,
  Internal = 13,
  Unavailable = 14,
  DataLoss = 15,
  Unauthenticated = 16,
}

export interface Details {
  [key: string]: unknown
  reason?: string
  message?: string
  resource?: string
  process?: string
}

export default class CodeError extends Error {
  code: ErrorCode
  details?: Details

  static code = ErrorCode

  static is(error: Error, code: ErrorCode): boolean {
    return error instanceof CodeError && error.code === code
  }

  static create(code: ErrorCode, details?: Details): CodeError {
    return new CodeError(code, details)
  }

  static fromJSON(json: any): CodeError {
    return new CodeError(json.code, json.details)
  }

  static fromResponse(response: Response): Promise<CodeError> {
    return response.json().then((json) => CodeError.fromJSON(json))
  }

  static async from(err: any): Promise<CodeError> {
    if (err instanceof CodeError) {
      return err
    }

    return new CodeError(ErrorCode.Unknown, {
      original: err,
      message: err.message,
    })
  }

  constructor(code: ErrorCode, details?: Details) {
    const message = `${getErrorCodeName(code)} - ${details?.message || 'unknown error'} | ${JSON.stringify(details)}`
    super(message)

    this.name = 'CodeError'
    this.code = code
    this.details = details
  }

  get data() {
    return {
      code: this.code,
      details: this.details,
      message: this.message,
    }
  }

  get say() {
    return this.details?.message || 'unknown error'
  }
}

function getErrorCodeName(code: ErrorCode): string {
  switch (code) {
    case ErrorCode.OK:
      return 'OK'
    case ErrorCode.Canceled:
      return 'Canceled'
    case ErrorCode.Unknown:
      return 'Unknown'
    case ErrorCode.InvalidArgument:
      return 'InvalidArgument'
    case ErrorCode.DeadlineExceeded:
      return 'DeadlineExceeded'
    case ErrorCode.NotFound:
      return 'NotFound'
    case ErrorCode.AlreadyExists:
      return 'AlreadyExists'
    case ErrorCode.PermissionDenied:
      return 'PermissionDenied'
    case ErrorCode.ResourceExhausted:
      return 'ResourceExhausted'
    case ErrorCode.FailedPrecondition:
      return 'FailedPrecondition'
    case ErrorCode.Aborted:
      return 'Aborted'
    case ErrorCode.OutOfRange:
      return 'OutOfRange'
    case ErrorCode.Unimplemented:
      return 'Unimplemented'
    case ErrorCode.Internal:
      return 'Internal'
    case ErrorCode.Unavailable:
      return 'Unavailable'
    case ErrorCode.DataLoss:
      return 'DataLoss'
    case ErrorCode.Unauthenticated:
      return 'Unauthenticated'
    default:
      return 'Unknown'
  }
}
